import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  PropertiesFilterParams,
  PropertyType,
  PropertiesResponse,
  CreatePropertyParams,
  UpdatePropertyParams
} from '@/types/property'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import {
  fetchProperties,
  fetchProperty,
  updateProperty,
  deleteProperty,
  createProperty,
  bulkDeleteProperties,
  sortProperty
} from '@/lib/api'
import { SortEnum, SortOrderEnum } from '@/enums'

// Single property state type
type PropertyStateType = {
  isLoading: boolean
  response: PropertyType | null
  error: {
    status?: number
    property: string
  } | null
}

// Properties list state type
type PropertiesListStateType = {
  isLoading: boolean
  response: PropertiesResponse | null
  error: {
    status?: number
    property: string
  } | null
}

type PropertiesStoreState = {
  // Properties list filters state
  filters: PropertiesFilterParams
  setFilters: (filters: Partial<PropertiesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // Properties list state
  propertiesListState: PropertiesListStateType
  fetchProperties: (params?: PropertiesFilterParams) => Promise<PropertiesResponse>
  resetPropertiesList: () => void

  // Sort property
  sortProperty: (propertyId: string, type: SortOrderEnum) => Promise<PropertyType>

  // Single property state
  propertyState: PropertyStateType
  getProperty: (propertyId: string) => Promise<PropertyType>
  createProperty: (params: CreatePropertyParams) => Promise<PropertyType>
  updateProperty: (propertyId: string, params: UpdatePropertyParams) => Promise<PropertyType>
  deleteProperty: (propertyId: string) => Promise<void>
  resetProperty: () => void

  // Selection operations
  selectedPropertyIds: string[]
  selectProperty: (id: string) => void
  deselectProperty: (id: string) => void
  togglePropertySelection: (id: string) => void
  selectAllProperties: (properties: PropertyType[]) => void
  deselectAllProperties: () => void
  isPropertySelected: (id: string) => boolean

  // Bulk delete operation
  bulkDeleteProperties: (ids: string[]) => Promise<BulkDeleteResponse>
}

const defaultFilters: PropertiesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'createdAt'
}

const initialPropertyState: PropertyStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialPropertiesListState: PropertiesListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const usePropertiesStore = create<PropertiesStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    propertyState: initialPropertyState,
    propertiesListState: initialPropertiesListState,
    selectedPropertyIds: [],

    setFilters: newFilters =>
      set(state => ({
        filters: { ...state.filters, ...newFilters }
      })),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: PropertiesFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as SortEnum
            break
          case 'sortBy':
            filters[key] = value as PropertiesFilterParams['sortBy']
            break
          case 'q':
          case 'createdAtStart':
          case 'createdAtEnd':
          case 'updatedAtStart':
          case 'updatedAtEnd':
            filters[key] = value
            break
          case 'createdUsers':
          case 'updatedUsers':
            filters[key] = value.split(',').filter(Boolean)
            break
        }
      })

      set(state => ({
        filters: { ...state.filters, ...filters }
      }))
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.append(key, value.join(','))
            }
          } else {
            params.append(key, value.toString())
          }
        }
      })

      return params
    },

    fetchProperties: async (params = {}) => {
      set(() => ({
        propertiesListState: {
          ...initialPropertiesListState,
          isLoading: true
        }
      }))

      try {
        const finalParams = { ...get().filters, ...params }
        const data = await fetchProperties(finalParams)

        set(() => ({
          propertiesListState: {
            ...initialPropertiesListState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          propertiesListState: {
            ...initialPropertiesListState,
            error: {
              status: error.response?.status || 500,
              property: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetPropertiesList: () => {
      set(() => ({
        propertiesListState: initialPropertiesListState
      }))
    },

    getProperty: async (propertyId: string) => {
      set(() => ({
        propertyState: {
          ...initialPropertyState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchProperty(propertyId)

        set(() => ({
          propertyState: {
            ...initialPropertyState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          propertyState: {
            ...initialPropertyState,
            error: {
              status: error.response?.status || 500,
              property: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    createProperty: async (params: CreatePropertyParams) => {
      set(() => ({
        propertyState: {
          ...initialPropertyState,
          isLoading: true
        }
      }))

      try {
        const data = await createProperty(params)

        set(() => ({
          propertyState: {
            ...initialPropertyState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          propertyState: {
            ...initialPropertyState,
            error: {
              status: error.response?.status || 500,
              property: getErrorMessage(error)
            }
          }
        }))

        // Handle 422 validation errors
        if (error.response?.status === 422) {
          const validationError = new Error('Validation failed')
          ;(validationError as any).response = error.response
          ;(validationError as any).isValidationError = true
          throw validationError
        }

        throw error
      }
    },

    updateProperty: async (propertyId: string, params: UpdatePropertyParams) => {
      set(() => ({
        propertyState: {
          ...get().propertyState,
          isLoading: true
        }
      }))

      try {
        const data = await updateProperty(propertyId, params)

        set(() => ({
          propertyState: {
            ...get().propertyState,
            isLoading: false,
            response: data,
            error: null
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          propertyState: {
            ...get().propertyState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              property: getErrorMessage(error)
            }
          }
        }))

        // 422 validation hatalarını sessizce handle et
        if (error.response?.status === 422) {
          // Validation hatalarını error olarak işaretle ama throw etme
          const validationError = new Error('Validation failed')
          ;(validationError as any).response = error.response
          ;(validationError as any).isValidationError = true
          throw validationError
        }

        throw error
      }
    },

    sortProperty: async (propertyId: string, type: SortOrderEnum) => {
      set(() => ({
        propertyState: {
          ...get().propertyState,
          isLoading: true
        }
      }))

      try {
        const data = await sortProperty(propertyId, type)

        set(() => ({
          propertyState: {
            ...get().propertyState,
            isLoading: false,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          propertyState: {
            ...initialPropertyState,
            error: {
              status: error.response?.status || 500,
              property: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    deleteProperty: async (propertyId: string) => {
      set(() => ({
        propertyState: {
          ...get().propertyState,
          isLoading: true
        }
      }))

      try {
        await deleteProperty(propertyId)

        set(() => ({
          propertyState: {
            ...get().propertyState,
            isLoading: false,
            error: null
          }
        }))
      } catch (error: any) {
        set(() => ({
          propertyState: {
            ...get().propertyState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              property: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetProperty: () => {
      set(() => ({
        propertyState: initialPropertyState
      }))
    },

    // Selection operations
    selectProperty: id => {
      set(state => {
        if (state.selectedPropertyIds.includes(id)) {
          return state
        }
        return { selectedPropertyIds: [...state.selectedPropertyIds, id] }
      })
    },

    deselectProperty: id => {
      set(state => ({
        selectedPropertyIds: state.selectedPropertyIds.filter(propertyId => propertyId !== id)
      }))
    },

    togglePropertySelection: id => {
      set(state => {
        if (state.selectedPropertyIds.includes(id)) {
          return { selectedPropertyIds: state.selectedPropertyIds.filter(propertyId => propertyId !== id) }
        }
        return { selectedPropertyIds: [...state.selectedPropertyIds, id] }
      })
    },

    selectAllProperties: properties => {
      set(() => ({
        selectedPropertyIds: properties.map(property => property.id)
      }))
    },

    deselectAllProperties: () => {
      set(() => ({ selectedPropertyIds: [] }))
    },

    isPropertySelected: id => {
      return get().selectedPropertyIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteProperties: async ids => {
      set(state => ({
        propertyState: {
          ...state.propertyState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteProperties({ ids })

        set(state => ({
          propertyState: {
            ...state.propertyState,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedPropertyIds: state.selectedPropertyIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error: any) {
        set(state => ({
          propertyState: {
            ...state.propertyState,
            error: {
              status: error.response?.status || 500,
              property: getErrorMessage(error)
            },
            isLoading: false
          }
        }))

        throw error
      }
    }
  }))
)

export default usePropertiesStore
